import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";

/* ------------------------- Importaciones internas ------------------------- */
import { REGEX_CUIT, REGEX_NUMBER } from "../../../../core/utils/regex";
import { checkField } from "../../../../core/utils/constantes";
import { handleFile } from "../../../../core/utils/file";
import BotonGenerico from "../../../../components/Button/Button";
import DialogFooter from "../../../../components/DialogFooter/DialogActions";
import { useAuth } from "../../../../contexts/AuthContext";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    minWidth: "800px",
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  titleText: {
    textAlign: "left",
    marginBottom: theme.spacing(3),
    fontSize: "1.25rem", // Tamaño de fuente mayor para el título
    textTransform: "none",
  },
  formRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1), // Espacio más amplio entre las filas
    gap: theme.spacing(1), // Espacio entre los elementos de cada fila
  },
  field: {
    width: "48%",
    fontSize: "0.6rem",
    textTransform: "none",
    marginBottom: theme.spacing(2),
  },
  descriptionField: {
    width: "50%",
    fontSize: "0.8rem",
    textTransform: "none",
    marginBottom: theme.spacing(2),
  },
}));

const IngresarComprobanteModal = ({
  open,
  comprobanteSelected,
  onClose,
  onSuccess,
  usuarioLocal,
  tipoComprobante,
  OS,
  editable = false,
}) => {
  const classes = useStyles();

  // Usuario local
  const { getUser } = useAuth();
  const user = getUser();

  const initialState = {
    fechaCreacion: new Date().toISOString().split("T")[0],
    fechaComprobante: new Date().toISOString().split("T")[0],
    tipoDeComprobante: "",
    cuit: "",
    sucursal: "",
    numero: "",
    observaciones: "",
    importe: "",
    estado: "Pendiente de pago",
    fechaPago: null,
    modalidadPago: "",
    nroReferenciaPago: "",
  };

  const readOnly = !editable || checkField(OS, "estadoOS", 3);

  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [archivoFile, setArchivoFile] = useState(null);
  const [adjuntoEliminado, setAdjuntoEliminado] = useState(false);
  const fileInputRef = useRef(null);

  // Si el adjunto del servidor fue marcado para eliminar, lo tratamos como ausente
  const tieneAdjunto = !adjuntoEliminado && !!comprobanteSelected?.adjunto;

  useEffect(() => {
    if (open) {
      if (comprobanteSelected) {
        setState({
          ...comprobanteSelected,
          tipoDeComprobante: comprobanteSelected.idTipoComprobante,
        });
        if (comprobanteSelected.archivoFile) {
          setArchivoFile(comprobanteSelected.archivoFile);
        }
      } else {
        setState({
          ...state,
          tipoDeComprobante: tipoComprobante?.Nombre,
        });
      }
    } else {
      setState(initialState);
      setErrors({});
      setArchivoFile(null);
      setAdjuntoEliminado(false);
    }
  }, [open]);

  const validarDatos = () => {
    const newErrors = {};
    if (!state.tipoDeComprobante)
      newErrors.tipoDeComprobante = "El tipo de comprobante es obligatorio.";
    if (!state.sucursal) newErrors.sucursal = "La sucursal es obligatoria.";
    if (!state.numero) newErrors.numero = "El número es obligatorio.";

    if (!state.importe) {
      newErrors.importe = "El importe total es obligatorio.";
    }

    if (!state.cuit) {
      newErrors.cuit = "El CUIT es obligatorio.";
    } else if (!/^\d{11}$/.test(state.cuit)) {
      newErrors.cuit = "El CUIT debe ser válido.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    const validacion = validarDatos();
    if (!validacion) return;

    if (!usuarioLocal) usuarioLocal = user;

    const comprobante = {
      fechaCreacion: state.fechaCreacion,
      fechaComprobante: state.fechaComprobante,
      cuit: state.cuit,
      sucursal: state.sucursal,
      numero: state.numero,
      observaciones: state.observaciones,
      importe: state.importe,
      estado: state.estado,
      fechaPago: state.fechaPago,
      modalidadPago: state.modalidadPago,
      nroReferenciaPago: state.nroReferenciaPago,
      idTipoComprobante: state.tipoDeComprobante,
      idUsuario: usuarioLocal.IdUsuario,
      archivoFile: archivoFile || undefined,
      adjunto: (adjuntoEliminado && !archivoFile) ? "" : undefined,
      esAdjunto: !!archivoFile || (!adjuntoEliminado && !!comprobanteSelected?.adjunto),
    };

    onSuccess(comprobante);
    close();
  };

  const handleArchivoClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleArchivoChange = async (e) => {
    const file = e.target.files?.[0];
    const input = e.target;
    if (!file) return;
    try {
      const result = await handleFile(file, { readOnly: true });
      setArchivoFile(result.file);
      // Si había un adjunto del servidor, marcarlo como reemplazado
      setAdjuntoEliminado(true);
    } catch (err) {
      console.error(err);
    } finally {
      if (input) input.value = "";
    }
  };

  const close = () => {
    setState(initialState);
    setErrors({});
    setArchivoFile(null);
    setAdjuntoEliminado(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md">
      <DialogTitle disableTypography>
        <Typography variant="h6" align="left">
          {state.estado === "Pendiente de pago"
            ? "Ingresar Comprobante"
            : "Viendo Comprobante"}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={close}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {/* Fila 1: Fecha comprobante, Tipo de comprobante */}
        <div className={classes.formRow}>
          <div className={classes.field}>
            <Typography variant="subtitle1">Fecha</Typography>
            <TextField
              variant="outlined"
              type="date"
              fullWidth
              value={state.fechaComprobante}
              onChange={(e) => {
                const value = e.target.value;
                setState((prev) => ({
                  ...prev,
                  fechaComprobante: value,
                }));
              }}
              disabled={readOnly}
            />
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">T. de Comprobante</Typography>
            <Select
              variant="outlined"
              fullWidth
              defaultValue={""}
              value={state.tipoDeComprobante || ""}
              onChange={(e) => {
                const value = e.target.value;
                setState((prev) => ({
                  ...prev,
                  tipoDeComprobante: value,
                }));
              }}
              error={!!errors.tipoDeComprobante || undefined}
              disabled={readOnly}
            >
              {tipoComprobante.map((tipo) => (
                <MenuItem
                  key={tipo.idtipo_comprobante}
                  value={tipo.idtipo_comprobante}
                >
                  {tipo.Nombre}
                </MenuItem>
              ))}
            </Select>
            {errors.tipoDeComprobante && (
              <Typography color="error" variant="body2">
                {errors.tipoDeComprobante}
              </Typography>
            )}
          </div>
        </div>
        {/* Fila 2: CUIT, Sucursal, Número */}
        <div className={classes.formRow}>
          <div className={classes.field}>
            <Typography variant="subtitle1">CUIT</Typography>
            <TextField
              variant="outlined"
              fullWidth
              type="tel"
              value={state.cuit || ""}
              inputProps={{
                maxLength: 11,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (REGEX_CUIT.test(value)) {
                  setState((prev) => ({ ...prev, cuit: value }));
                }
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={!!errors.cuit || undefined}
              disabled={readOnly}
            />
            {errors.cuit && (
              <Typography color="error" variant="body2">
                {errors.cuit}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Sucursal</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={state.sucursal || ""}
              onChange={(e) => {
                const value = e.target.value;
                setState((prev) => ({ ...prev, sucursal: value }));
              }}
              error={!!errors.sucursal || undefined}
              disabled={readOnly}
            />
            {errors.sucursal && (
              <Typography color="error" variant="body2">
                {errors.sucursal}
              </Typography>
            )}
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Número</Typography>
            <TextField
              variant="outlined"
              fullWidth
              type="tel"
              value={state.numero || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (REGEX_NUMBER.test(value)) {
                  setState((prev) => ({ ...prev, numero: value }));
                }
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={!!errors.numero || undefined}
              disabled={readOnly}
            />
            {errors.numero && (
              <Typography color="error" variant="body2">
                {errors.numero}
              </Typography>
            )}
          </div>
        </div>
        {/* Fila 3: Observaciones, Importe */}
        <div className={classes.formRow}>
          <div className={classes.descriptionField}>
            <Typography variant="subtitle1">Observaciones</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={state.observaciones || ""}
              onChange={(e) => {
                const value = e.target.value;
                setState((prev) => ({ ...prev, observaciones: value }));
              }}
              disabled={readOnly}
            />
          </div>
          <div className={classes.field}>
            <Typography variant="subtitle1">Importe Total</Typography>
            <TextField
              variant="outlined"
              fullWidth
              type="number"
              value={state.importe || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (REGEX_NUMBER.test(value)) {
                  setState((prev) => ({ ...prev, importe: value }));
                }
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={!!errors.importe || undefined}
              disabled={readOnly}
            />
            {errors.importe && (
              <Typography color="error" variant="body2">
                {errors.importe}
              </Typography>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "100%", gap: 0 }}>
          {/* Fila superior: botones de adjunto */}
          <div style={{ display: "flex", gap: 8, paddingBottom: 10, borderBottom: "1px solid #e0e0e0", width: "100%", justifyContent: "flex-end" }}>
            {/* Ver: siempre que haya archivo (servidor o memoria) */}
            {(tieneAdjunto || archivoFile) && (
              <BotonGenerico
                tipo="adjuntar"
                text="Ver comprobante"
                color="primary"
                onClick={() => {
                  if (tieneAdjunto) {
                    window.open(`${process.env.REACT_APP_SERVER_URL}${comprobanteSelected.adjunto}`, "_blank");
                  } else if (archivoFile) {
                    const url = URL.createObjectURL(archivoFile);
                    window.open(url, "_blank");
                    setTimeout(() => URL.revokeObjectURL(url), 10000);
                  }
                }}
              />
            )}
            {/* En modo edición: Reemplazar + Eliminar si hay archivo, o Adjuntar si no hay */}
            {!readOnly && (tieneAdjunto || archivoFile) && (
              <BotonGenerico
                tipo="adjuntar"
                text="Reemplazar comprobante"
                color="primary"
                onClick={handleArchivoClick}
              />
            )}
            {!readOnly && (tieneAdjunto || archivoFile) && (
              <BotonGenerico
                tipo="rechazar"
                color="secondary"
                onClick={() => { setArchivoFile(null); setAdjuntoEliminado(true); }}
                text="Eliminar comprobante"
              />
            )}
            {!readOnly && !tieneAdjunto && !archivoFile && (
              <BotonGenerico
                tipo="adjuntar"
                text="Adjuntar comprobante"
                color="primary"
                onClick={handleArchivoClick}
              />
            )}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleArchivoChange}
              accept=".pdf"
            />
          </div>
          {/* Fila inferior: guardar / cancelar */}
          <div style={{ display: "flex", gap: 8, paddingTop: 10 }}>
            {!readOnly && (
              <BotonGenerico
                tipo="guardar"
                color="primary"
                onClick={handleSave}
                text="Guardar"
              />
            )}
            <BotonGenerico
              tipo="cancelar"
              color="secondary"
              onClick={onClose}
              text={readOnly ? "Cerrar" : "Cancelar"}
            />
          </div>
        </div>
      </DialogFooter>
    </Dialog>
  );
};

export default IngresarComprobanteModal;
